import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useEventContext } from "Bands/contexts";
import { clearDataByKey, dataMetaSelector } from "Bands/state";
import {
  createEventDetailsExport,
  getEventDetailsExport,
} from "Bands/state/data/actions/eventDetailsExport";
import { eventDetailsExportSelector } from "Bands/state/data/selectors/eventDetailsExport";
import { BAND_ACTIONS, IEventDetailsExportParams, IScheduleBlock, PDFStatus } from "Bands/types";
import { useClearActionMetaOnUnMount } from "Bands/utils";
import { copy } from "shared/assets";
import { FormBuilder, Modal } from "shared/components";
import { API_ENDPOINTS } from "shared/constants";
import { Spacer } from "shared/layout";
import { IAPIState, IDataKeys, IFormButtons } from "shared/types";
import { downloadFile } from "shared/utils";
import { getFields } from "./formData";

const downloadCopy = copy.scheduleBlock.download;

interface DownloadScheduleBlockModalProps {
  block: IScheduleBlock;
  handleClose: (block: IScheduleBlock | null) => void;
}

export const DownloadScheduleBlockModal: React.FC<DownloadScheduleBlockModalProps> = ({
  block,
  handleClose,
}) => {
  const dispatch = useDispatch();
  const { eventId } = useEventContext();

  const { api, id: createdScheduleExportId } = useSelector(
    dataMetaSelector(BAND_ACTIONS.createEventDetailsExport)
  );

  const { api: getExportApi } = useSelector(dataMetaSelector(BAND_ACTIONS.getEventDetailsExport));

  const eDExportSelector = useMemo(() => eventDetailsExportSelector(), []);
  const eventDetailsExport = useSelector(eDExportSelector);

  useClearActionMetaOnUnMount(BAND_ACTIONS.createEventDetailsExport);

  const handleModalClose = useCallback(() => {
    dispatch(clearDataByKey(IDataKeys.eventDetailsExport));
    handleClose(null);
  }, [dispatch, handleClose]);

  const handleSubmit = (data: IEventDetailsExportParams) => {
    dispatch(
      createEventDetailsExport({
        ...data,
        showMyPersonalSchedule: !!data.showMyPersonalSchedule,
        eventBlocks: [API_ENDPOINTS.EVENT_SCHEDULE_BLOCK(block.id)],
        event: API_ENDPOINTS.EVENT(eventId!),
      })
    );
  };

  const isSubmitting = useMemo(
    () =>
      api?.status === IAPIState.pending ||
      (api?.status === IAPIState.succeeded &&
        eventDetailsExport?.pdfStatus !== PDFStatus.completed),
    [api?.status, eventDetailsExport?.pdfStatus]
  );

  const buttons: IFormButtons = useMemo(
    () => ({
      primary: {
        children: downloadCopy.buttons.download,
        disabled: isSubmitting,
      },
    }),
    [isSubmitting]
  );

  useEffect(() => {
    let timeout: any = null;
    if (
      api?.status === IAPIState.succeeded &&
      getExportApi?.status !== IAPIState.pending &&
      createdScheduleExportId &&
      eventDetailsExport?.pdfStatus !== PDFStatus.completed
    ) {
      timeout = setTimeout(() => {
        dispatch(getEventDetailsExport(createdScheduleExportId));
      }, 2000);
    }

    if (
      api?.status === IAPIState.succeeded &&
      eventDetailsExport?.pdfStatus === PDFStatus.completed
    ) {
      downloadFile(eventDetailsExport.pdfUrl!, eventDetailsExport.title);

      handleModalClose();
    }

    return () => timeout && clearTimeout(timeout);
  }, [
    api?.status,
    getExportApi?.status,
    createdScheduleExportId,
    eventDetailsExport,
    dispatch,
    handleModalClose,
  ]);

  const defaultValues = useMemo(
    () => ({
      title: block.title,
      paperSize: "a4",
    }),
    [block.title]
  );

  return (
    <Modal
      title={downloadCopy.title.replace("%b", block.title)}
      showCloseButton
      handleClose={handleModalClose}
    >
      <Spacer size="m">
        <FormBuilder
          defaultValues={defaultValues}
          fields={getFields()}
          onSubmit={handleSubmit}
          buttons={buttons}
          isSubmitting={isSubmitting}
        />
      </Spacer>
    </Modal>
  );
};
